import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { GuestOpacResourceSummary } from "@/lib/api/guest-opac";
import { canHoverPrefetch, prefetchGuestResource } from "@/lib/guest-opac-prefetch";
import { cn } from "@/lib/utils";

interface GuestCatalogListRowProps {
  item: GuestOpacResourceSummary;
  tenantSlug?: string;
}

export function GuestCatalogListRow({ item, tenantSlug }: GuestCatalogListRowProps) {
  const authors = item.authors?.length ? item.authors.join(", ") : "Unknown author";
  const available = item.availability?.availableCopies ?? 0;
  const total = item.availability?.totalCopies ?? 0;

  return (
    <Link
      to={`/opac/view/${item.publicId}`}
      className="group flex gap-4 p-4 transition-colors duration-200 hover:bg-gray-50"
      onMouseEnter={() => {
        if (tenantSlug && canHoverPrefetch()) prefetchGuestResource(item.publicId, tenantSlug);
      }}
    >
      <div className="h-20 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
        {item.coverUrl ? (
          <img src={item.coverUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <BookOpen className="h-8 w-8 opacity-40" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-start gap-2">
          <h3 className="line-clamp-2 font-semibold text-gray-900 transition-colors duration-200 group-hover:text-primary">
            {item.title}
          </h3>
          <Badge variant="secondary" className="shrink-0 text-[10px] capitalize">
            {item.type}
          </Badge>
        </div>
        <p className="line-clamp-1 text-sm text-gray-500">{authors}</p>
        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-500">
          {item.publisher && <span>{item.publisher}</span>}
          {item.type !== "digital" && total > 0 && (
            <span className={cn(available > 0 ? "text-emerald-700" : "text-gray-400")}>
              {available > 0 ? `${available} available` : "Unavailable"}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
